/**
 * Agency portal — Portfolio (SYR-85).
 *
 * The home surface: portfolio KPIs, rule-based needs-attention strip, and the
 * ranked client grid with live BE scores, deltas, bucket benchmarks and
 * sparklines. Ranking never mixes mock and real orgs (portfolios are
 * homogeneous by domain rule).
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Spin, message } from 'antd';
import { PlusOutlined, RightOutlined, WarningOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../hooks/useAuth';
import { useAgencyContext } from '../../hooks/useAgencyContext';
import { useImpersonation } from '../../hooks/useImpersonation';
import {
    useGetAgencyPortfolioAnalyticsQuery,
    useRemoveAgencyClientMutation,
    type IAgencyAnalyticsBrand,
    type IAgencyAnalyticsClient,
} from '../../services/nestjs-base-api';
import { THEME } from '../../components/MarketerView/config/theme';
import {
    BAND_PALETTE,
    bandFromScore,
    formatBipolarScore,
} from '../../components/MarketerView/config/bandColours';
import { Card } from '../../components/MarketerView/atoms';
import { useAgencyScope, AGENCY_PERIOD_LABELS } from '../shell/AgencyScopeContext';
import {
    AgencyEyebrow,
    AgencyPageHeader,
    SignalChip,
    StatTile,
    primaryBtnStyle,
} from '../components/agencyUi';
import { ClientCard } from '../components/ClientCard';
import { CreateClientOrgModal } from '../components/CreateClientOrgModal';

interface BrandRow {
    client: IAgencyAnalyticsClient;
    brand: IAgencyAnalyticsBrand;
}

export const AgencyPortfolioPage: React.FunctionComponent = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { period } = useAgencyScope();
    const { switchToClient } = useAgencyContext();
    const { isImpersonating } = useImpersonation();
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [removeClient] = useRemoveAgencyClientMutation();

    const { data, isLoading, isFetching } = useGetAgencyPortfolioAnalyticsQuery({
        jwtToken: user?.jwtToken || '',
        period,
    });

    const rows = useMemo<BrandRow[]>(() => {
        const flat: BrandRow[] = [];
        for (const client of data?.clients || []) {
            for (const brand of client.brands) {
                flat.push({ client, brand });
            }
        }
        return flat;
    }, [data]);

    const ranked = useMemo(
        () =>
            [...rows]
                .filter((r) => r.brand.equity.score !== null)
                .sort((a, b) => (b.brand.equity.score ?? 0) - (a.brand.equity.score ?? 0)),
        [rows],
    );
    const unranked = useMemo(
        () => rows.filter((r) => r.brand.equity.score === null),
        [rows],
    );

    const attention = useMemo(
        () =>
            rows.filter((r) =>
                r.brand.signals.some((s) => s.severity === 'high' || s.severity === 'medium'),
            ),
        [rows],
    );

    const portfolioAverage = useMemo(() => {
        const scores = ranked.map((r) => r.brand.equity.score as number);
        if (scores.length === 0) return null;
        return scores.reduce((a, b) => a + b, 0) / scores.length;
    }, [ranked]);

    const totalResponsesInPeriod = rows.reduce(
        (sum, r) => sum + r.brand.responsesInPeriod,
        0,
    );

    const periodLabel = intl.formatMessage({
        id: AGENCY_PERIOD_LABELS[period].id,
        defaultMessage: AGENCY_PERIOD_LABELS[period].def,
    });

    const handleRemove = (client: IAgencyAnalyticsClient): void => {
        Modal.confirm({
            title: intl.formatMessage({
                id: 'agency.portfolio.removeTitle',
                defaultMessage: 'Remove client?',
            }),
            content: intl.formatMessage(
                {
                    id: 'agency.portfolio.removeContent',
                    defaultMessage:
                        'This ends your agency relationship with {name}. Their account stays intact.',
                },
                { name: client.clientOrganisationName },
            ),
            okText: intl.formatMessage({
                id: 'agency.portfolio.removeOk',
                defaultMessage: 'Remove',
            }),
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await removeClient({
                        jwtToken: user?.jwtToken || '',
                        clientId: client.agencyClientId,
                    }).unwrap();
                    message.success(
                        intl.formatMessage({
                            id: 'agency.portfolio.removeSuccess',
                            defaultMessage: 'Client removed',
                        }),
                    );
                } catch {
                    message.error(
                        intl.formatMessage({
                            id: 'agency.portfolio.removeError',
                            defaultMessage: 'Failed to remove client',
                        }),
                    );
                }
            },
        });
    };

    const averageBand = bandFromScore(portfolioAverage);

    if (isLoading) {
        return (
            <div>
                <AgencyPageHeader
                    title={
                        <FormattedMessage id="agency.portfolio.title" defaultMessage="Portfolio" />
                    }
                    subtitle={
                        <FormattedMessage
                            id="agency.portfolio.loading"
                            defaultMessage="Calculating brand equity across your clients…"
                        />
                    }
                />
                <div style={{ padding: 96, textAlign: 'center' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16, color: THEME.textSecondary, fontSize: 14 }}>
                        <FormattedMessage
                            id="agency.portfolio.loadingDetail"
                            defaultMessage="First load computes fresh scores for every client — this can take a moment."
                        />
                    </div>
                </div>
            </div>
        );
    }

    const empty = rows.length === 0;

    return (
        <div>
            <AgencyPageHeader
                title={<FormattedMessage id="agency.portfolio.title" defaultMessage="Portfolio" />}
                subtitle={
                    <FormattedMessage
                        id="agency.portfolio.subtitle"
                        defaultMessage="Brand equity across every client — {period}."
                        values={{ period: periodLabel.toLowerCase() }}
                    />
                }
                extra={
                    <button
                        type="button"
                        style={primaryBtnStyle}
                        onClick={() => setCreateModalOpen(true)}
                    >
                        <PlusOutlined />
                        <FormattedMessage
                            id="agency.portfolio.addClient"
                            defaultMessage="Add client"
                        />
                    </button>
                }
            />

            {empty ? (
                <Card>
                    <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
                            <FormattedMessage
                                id="agency.portfolio.emptyTitle"
                                defaultMessage="Get started with your first client"
                            />
                        </div>
                        <p style={{ color: THEME.textSecondary, maxWidth: 420, margin: '0 auto 24px' }}>
                            <FormattedMessage
                                id="agency.portfolio.emptyDetail"
                                defaultMessage="Create a new client organisation to start tracking and comparing brand equity across your portfolio."
                            />
                        </p>
                        <button
                            type="button"
                            style={primaryBtnStyle}
                            onClick={() => setCreateModalOpen(true)}
                        >
                            <PlusOutlined />
                            <FormattedMessage
                                id="agency.portfolio.createClient"
                                defaultMessage="Create client"
                            />
                        </button>
                    </div>
                </Card>
            ) : (
                <>
                    {/* KPI band */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                            gap: 16,
                            marginBottom: 28,
                        }}
                    >
                        <StatTile
                            label={
                                <FormattedMessage
                                    id="agency.portfolio.kpiClients"
                                    defaultMessage="Clients"
                                />
                            }
                            value={data?.clients.length ?? 0}
                        />
                        <StatTile
                            label={
                                <FormattedMessage
                                    id="agency.portfolio.kpiAverage"
                                    defaultMessage="Portfolio equity"
                                />
                            }
                            value={
                                portfolioAverage !== null
                                    ? formatBipolarScore(portfolioAverage)
                                    : '—'
                            }
                            accent={
                                portfolioAverage !== null
                                    ? BAND_PALETTE[averageBand].fg
                                    : undefined
                            }
                            detail={
                                <FormattedMessage
                                    id="agency.portfolio.kpiAverageDetail"
                                    defaultMessage="Average across scored clients"
                                />
                            }
                        />
                        <StatTile
                            label={
                                <FormattedMessage
                                    id="agency.portfolio.kpiResponses"
                                    defaultMessage="Responses"
                                />
                            }
                            value={totalResponsesInPeriod.toLocaleString()}
                            detail={periodLabel}
                        />
                        <StatTile
                            label={
                                <FormattedMessage
                                    id="agency.portfolio.kpiAttention"
                                    defaultMessage="Need attention"
                                />
                            }
                            value={attention.length}
                            accent={
                                attention.length > 0 ? BAND_PALETTE.watch.fg : BAND_PALETTE.strong.fg
                            }
                            detail={
                                attention.length === 0 ? (
                                    <FormattedMessage
                                        id="agency.portfolio.kpiAttentionClear"
                                        defaultMessage="All clients healthy"
                                    />
                                ) : undefined
                            }
                        />
                    </div>

                    {/* Needs attention strip */}
                    {attention.length > 0 && (
                        <div style={{ marginBottom: 28 }}>
                            <AgencyEyebrow>
                                <WarningOutlined
                                    style={{ marginRight: 6, color: BAND_PALETTE.watch.fg }}
                                />
                                <FormattedMessage
                                    id="agency.portfolio.attentionTitle"
                                    defaultMessage="Needs attention"
                                />
                            </AgencyEyebrow>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: `repeat(${Math.min(
                                        attention.length,
                                        3,
                                    )}, minmax(0, 1fr))`,
                                    gap: 16,
                                }}
                            >
                                {attention.map(({ client, brand }) => (
                                    <div
                                        key={brand.brandId}
                                        className="lift"
                                        onClick={() =>
                                            navigate(`/agency/clients/${client.clientOrganisationId}`)
                                        }
                                        style={{
                                            background: THEME.bgCard,
                                            border: `1px solid ${BAND_PALETTE.watch.line}`,
                                            borderLeft: `4px solid ${BAND_PALETTE.watch.fg}`,
                                            borderRadius: 12,
                                            padding: '14px 18px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 12,
                                        }}
                                    >
                                        <div style={{ minWidth: 0 }}>
                                            <div
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 14.5,
                                                    marginBottom: 6,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {client.clientOrganisationName}
                                            </div>
                                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                {brand.signals.map((s) => (
                                                    <SignalChip key={s.type} signal={s} />
                                                ))}
                                            </div>
                                        </div>
                                        <RightOutlined
                                            style={{ color: THEME.textMuted, fontSize: 12 }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Ranked client grid */}
                    <AgencyEyebrow>
                        <FormattedMessage
                            id="agency.portfolio.rankingTitle"
                            defaultMessage="Client ranking"
                        />
                        {isFetching && (
                            <Spin size="small" style={{ marginLeft: 10 }} />
                        )}
                    </AgencyEyebrow>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {ranked.map(({ client, brand }, idx) => (
                            <ClientCard
                                key={brand.brandId}
                                client={client}
                                brand={brand}
                                rank={idx + 1}
                                period={period}
                                onOpenDashboard={switchToClient}
                                onRemove={handleRemove}
                                openDashboardDisabled={isImpersonating}
                            />
                        ))}
                        {unranked.map(({ client, brand }) => (
                            <ClientCard
                                key={brand.brandId}
                                client={client}
                                brand={brand}
                                rank={null}
                                period={period}
                                onOpenDashboard={switchToClient}
                                onRemove={handleRemove}
                                openDashboardDisabled={isImpersonating}
                            />
                        ))}
                    </div>
                </>
            )}

            <CreateClientOrgModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} />
        </div>
    );
};
